import { CheckCircle2, XCircle } from 'lucide-react';

import { Button } from '@/shared/ui/shadcn/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/shadcn/card';

export type OnboardingResultKind = 'success' | 'error';

export interface OnboardingResultStepProps {
  kind: OnboardingResultKind;
  errorMessage?: string | null;
  onComplete?: () => void;
  onRetry?: () => void;
  retryLabel?: string;
  completeLabel?: string;
}

export function OnboardingResultStep(props: OnboardingResultStepProps) {
  const { kind, errorMessage, onComplete, onRetry, retryLabel, completeLabel } = props;

  if (kind === 'success') {
    return (
      <Card>
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle>You're all set!</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground text-center text-sm">
          <p>Your DoseSpot prescriber onboarding is complete.</p>
          <p className="mt-1">You can now write and send prescriptions.</p>
        </CardContent>
        {onComplete ? (
          <CardFooter className="justify-center">
            <Button type="button" radius="pill" onClick={onComplete}>
              Start prescribing
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="items-center text-center">
        <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
          <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
        </div>
        <CardTitle>Onboarding failed</CardTitle>
      </CardHeader>
      <CardContent className="text-muted-foreground text-center text-sm">
        <p>Something went wrong during the onboarding process.</p>
        {errorMessage ? (
          <p className="mt-2 rounded-md bg-red-50 p-2 text-red-700 dark:bg-red-900/20 dark:text-red-300">
            {errorMessage}
          </p>
        ) : (
          <p className="mt-1">Please try again or contact support if the issue persists.</p>
        )}
      </CardContent>
      <CardFooter className="justify-center gap-2">
        {onRetry ? (
          <Button type="button" variant="secondary" onClick={onRetry}>
            {retryLabel ?? 'Try again'}
          </Button>
        ) : null}
        {onComplete ? (
          <Button type="button" variant="outline" onClick={onComplete}>
            {completeLabel ?? 'Close'}
          </Button>
        ) : null}
      </CardFooter>
    </Card>
  );
}
